<template>
  <Line :data="chartData" :options="chartOptions"/>
</template>

<script>
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import {Line} from 'vue-chartjs'
import {externalTooltipHandler} from './chartConfig'

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Title,
    Tooltip,
    Legend
)

const BRAND = '#153D33';
const SIGNAL = '#C3F400';

export default {
  components: {
    Line
  },
  props: {
    labels: {
      type: Object,
      required: true
    },
    data: {
      type: Object,
      required: true
    },
    /* What unit `data` arrives in.
     *
     * The Strong Wool Index API reports cents per kg clean, so this component has
     * always divided by 100. The public preview API reports DOLLARS already, and
     * silently dividing those by 100 turned $6.20 into $0.06. The unit is now
     * stated by the caller rather than assumed, and defaults to cents so the
     * existing index chart is unaffected. */
    unit: {
      type: String,
      default: 'cents',
      validator: (value) => ['cents', 'dollars'].includes(value)
    }
  },
  computed: {
    /* The API reports cents per kg clean, but the hero, the strip and every
     * other figure on the site are dollars. Converting here means the chart
     * cannot contradict the headline number. */
    dollars() {
      const divisor = this.unit === 'dollars' ? 1 : 100;
      return Array.from(this.data).map(v => v / divisor);
    },

    lastIndex() {
      return this.dollars.length - 1;
    },

    chartData() {
      const last = this.lastIndex;

      return {
        labels: this.labels,
        datasets: [
          {
            label: 'NZ$ per kg clean',
            data: this.dollars,
            fill: true,
            backgroundColor: (ctx) => {
              const {ctx: canvas, chartArea} = ctx.chart;
              if (!chartArea) return 'rgba(21, 61, 51, 0.10)';

              const gradient = canvas.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0, 'rgba(21, 61, 51, 0.20)');
              gradient.addColorStop(1, 'rgba(21, 61, 51, 0)');
              return gradient;
            },
            borderColor: BRAND,
            borderWidth: 2,
            tension: 0.15,
            /* Only the latest reading carries a marker, so the eye lands on
             * "where it is now" rather than on 700 identical dots. */
            pointRadius: (ctx) => ctx.dataIndex === last ? 5 : 0,
            pointHoverRadius: 5,
            pointBackgroundColor: (ctx) => ctx.dataIndex === last ? SIGNAL : BRAND,
            pointBorderColor: BRAND,
            pointBorderWidth: 2,
          }
        ]
      }
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          /* A single series needs no legend; the axis label already says what
           * the line is. */
          legend: {
            display: false
          },
          tooltip: {
            enabled: false,
            position: 'nearest',
            external: externalTooltipHandler,
            callbacks: {
              label: (ctx) => ' $' + Number(ctx.parsed.y).toFixed(2) + ' per kg clean'
            }
          }
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        scales: {
          x: {
            display: true,
            grid: {display: false},
            ticks: {
              /* Rotated, every-point labels were unreadable over a long range.
               * Sparse and horizontal; the precise date lives in the tooltip. */
              autoSkip: true,
              maxTicksLimit: 8,
              maxRotation: 0,
              minRotation: 0,
              color: '#5B6876',
              font: {size: 12}
            }
          },
          y: {
            display: true,
            title: {
              display: true,
              text: 'NZ$ per kg clean',
              color: '#5B6876',
              font: {size: 12}
            },
            grid: {color: 'rgba(14, 21, 18, 0.06)'},
            ticks: {
              color: '#5B6876',
              font: {size: 12},
              callback: (value) => '$' + Number(value).toFixed(2)
            },
            /* Anchoring at zero flattens the trend; the series never
             * approaches it. */
            grace: '8%'
          }
        }
      }
    }
  }
}
</script>
